import { PropsWithChildren } from 'react';

import AlertStore, { alertStore } from '@/alert/stores/AlertStore';
import AnalyticsStore, { analyticsStore } from '@/analytics/store/AnalyticsStore';
import BillingStore, { billingStore } from '@/billing/stores/BillingStore';
import CollectionPreviewStore, { collectionPreviewStore } from '@/collection/stores/CollectionPreviewStore';
import CollectionsStore, { collectionsStore } from '@/collection/stores/CollectionsStore';
import ModalStore, { modalStore } from '@/common/components/Modal/ModalStore';
import { StoresProvider } from '@/common/components/StoresProvider/StoresProvider';
import { Poller, poller } from '@/common/services/Poller/Poller';
import AppStore, { appStore } from '@/common/stores/AppStore';
import ConfigurationStore, { configurationStore } from '@/common/stores/ConfigurationStore';
import GlobalSearchConfigStore, { globalSearchConfigStore } from '@/common/stores/GlobalSearchConfigStore';
import PresentationStore, { presentationStore } from '@/common/stores/PresentationStore';
import ReferenceStore, { referenceStore } from '@/common/stores/ReferenceStore';
import separateWindowStore, { SeparateWindowStore } from '@/common/stores/SeparateWindowStore';
import uploadFileStore, { UploadFileStore } from '@/common/stores/UploadFileStore';
import ComparisonStore, { comparisonStore } from '@/comparison/stores/ComparisonStore';
import ContactsStore, { contactsStore } from '@/contacts/stores/ContactsStore';
import ServicesStore, { servicesStore } from '@/conversion/stores/ServicesStore';
import DebugSettingsStore, { debugSettingsStore } from '@/debug/stores/DebugSettingsStore';
import { DriveModifierStore, driveModifierStore } from '@/drive/stores/DriveModifierStore/DriveModifierStore';
import DriveTreeStore, { driveTreeStore } from '@/drive/stores/DriveTreeStore';
import PinnedItemsDtoStore, { pinnedItemsDtoStore } from '@/forwarding/stores/PinnedItemsDtoStore';
import PinnedItemsStore, { pinnedItemsStore } from '@/forwarding/stores/PinnedItemsStore';
import NetBoxStore, { netBoxStore } from '@/netbox/stores/NetBoxStore';
import { EntityAccessEventEmitter } from '@/security/services/EntityAccessEventEmitter';
import PendingSpectrumTasksStore, { pendingSpectrumTasksStore } from '@/spectrum/stores/PendingSpectrumTasksStore';
import UserStore, { userStore } from '@/user/stores/UserStore';

interface Store {
  billingStore: BillingStore;
  configurationStore: ConfigurationStore;
  appStore: AppStore;
  userStore: UserStore;
  comparisonStore: ComparisonStore;
  collectionsStore: CollectionsStore;
  analyticsStore: AnalyticsStore;
  netBoxStore: NetBoxStore;
  referenceStore: ReferenceStore;
  presentationStore: PresentationStore;
  separateWindowStore: SeparateWindowStore;
  contactsStore: ContactsStore;
  servicesStore: ServicesStore;
  uploadFileStore: UploadFileStore;
  globalSearchConfigStore: GlobalSearchConfigStore;
  pinnedItemsStore: PinnedItemsStore;
  pinnedItemsDtoStore: PinnedItemsDtoStore;
  driveModifierStore: DriveModifierStore;
  driveTreeStore: DriveTreeStore;
  alertStore: AlertStore;
  pendingSpectrumTasksStore: PendingSpectrumTasksStore;
  collectionPreviewStore: CollectionPreviewStore;
  modalStore: ModalStore;
  entityAccessEventEmitter: EntityAccessEventEmitter;
  debugSettingsStore: DebugSettingsStore;
  poller: Poller;
}

const store: Store = {
  alertStore,
  billingStore,
  configurationStore,
  appStore,
  userStore,
  comparisonStore,
  collectionsStore,
  analyticsStore,
  netBoxStore,
  referenceStore,
  presentationStore,
  separateWindowStore,
  contactsStore,
  servicesStore,
  uploadFileStore,
  globalSearchConfigStore,
  pinnedItemsStore,
  pinnedItemsDtoStore,
  driveModifierStore,
  driveTreeStore,
  collectionPreviewStore,
  pendingSpectrumTasksStore,
  modalStore,
  entityAccessEventEmitter: new EntityAccessEventEmitter(),
  debugSettingsStore,
  poller,
};

export function StoreProvider({ children }: PropsWithChildren<unknown>) {
  return <StoresProvider stores={store}>{children}</StoresProvider>;
}

export type { Store };
export default store;
